/* eslint-disable no-unwrapped-jsx/no-unwrapped-jsx-text */
import React from 'react';
import { Utils } from 'Utils/utils';
import { observer } from 'mobx-react';
import { advSearchState } from 'Stores/advSearch';
import { CommonPopupWithImageComponent } from 'Components/common/commonPopupWithImageComponent';
import { ReactComponent as PurchaseSuccess } from 'Assets/svg/new_icons/purchaseSuccess.svg';
import { ReactComponent as CSVExportIcon } from 'Assets/svg/new_icons/csvExport.svg';
import { ReactComponent as ErrorIcon } from 'Assets/svg/new_icons/errorTriangle.svg';
import { EXPORT_SOURCE } from 'Utils/constants';
import { exportLeadsState } from './exportLeads';

const { ADVANCED_SEARCH } = EXPORT_SOURCE;

const UserEmailElement = ({ userEmail }) => (
  <a href={`mailto:${userEmail}`} target="_blank" rel="noopener noreferrer">
    {userEmail}
  </a>
);

const ErrorDescription = () => {
  const mailElement = <UserEmailElement userEmail="support@adapt.io" />;
  return (
    <>
      Please try again after sometime. <br />
      Contact {mailElement} if the problem persists
    </>
  );
};

function DescriptionNormal(props) {
  const { userEmail, flags } = props;
  const { isPurchase, isCSVExport, isCRMExport } = flags;

  const userEmailElement = <UserEmailElement userEmail={userEmail} />;
  const fewMinutesText = 'Usually, this takes a few minutes.';
  return isPurchase ? (
    <>
      We will email you at {userEmailElement} once the list is ready. Usually,
      this takes a few minutes.
    </>
  ) : isCSVExport ? (
    <>
      We will email you at {userEmailElement} with the link to download the file
      once the export is complete.<br /> Usually, this takes a few minutes.
    </>
  ) : isCRMExport ? (
    <>
      We will email you at {userEmailElement} once the export is complete.{' '}
      {fewMinutesText}
    </>
  ) : (
    ''
  );
}

function DescriptionZB(props) {
  const { userEmail, flags } = props;
  const { isPurchase, isCSVExport, isCRMExport } = flags;

  const userEmailElement = (
    <a href={`mailto:${userEmail}`} target="_blank" rel="noopener noreferrer">
      {userEmail}
    </a>
  );

  const rightInboxText = 'This is to ensure you hit the right inbox!';
  return isPurchase ? (
    <>
      <div>
        Once the contacts are added to the list, we&#39;ll send you an email
        within few minutes with link directly to the updated list.
      </div>
    </>
  ) : isCSVExport ? (
    <>
      <div>
        Your list is going through ZeroBounce real-time verification. The
        download link will be sent to {userEmailElement} in a few minutes.{' '}
        {rightInboxText}
      </div>
    </>
  ) : isCRMExport ? (
    <>
      <div>
        Your list is going through ZeroBounce real-time verification. We will
        email you at {userEmailElement} in a few minutes. {rightInboxText}
      </div>
    </>
  ) : (
    ''
  );
}

const RealTimeVerificationMessage = () => {
  return (
    <>
      The actual count may vary post verification. Don&#39;t worry! Your credits
      will be deducted only for the verified contacts.
    </>
  );
};

const WithoutEmailMessage = () => {
  return <>Contacts without email addresses will not be exported.</>;
};

const NoteList = props => {
  const { showVerificationNote, showWithoutEmailNote } = props;

  return (
    <section className="note-wrapper">
      <div className="note-title">Note :</div>
      <ol
        className={
          showVerificationNote && showWithoutEmailNote ? '' : 'no-list-style'
        }
      >
        {showVerificationNote && (
          <li>
            <RealTimeVerificationMessage />
          </li>
        )}
        {showWithoutEmailNote && (
          <li>
            <WithoutEmailMessage />
          </li>
        )}
      </ol>
    </section>
  );
};

const AsyncPurchaseExportMsgPopupComponent = observer(props => {
  const { asyncPurchaseOrExportMsg } = advSearchState;
  const { exportSource } = exportLeadsState;
  if (asyncPurchaseOrExportMsg !== '') {
    const handleHoldSuccessOkClick = () => {
      advSearchState.setAsyncPurchaseOrExportMsg('');
    };
    const userEmail = Utils.getUserEmail();

    const isZBEnabled = Utils.isZeroBounceEnabled();

    const isPurchase = asyncPurchaseOrExportMsg === 'PURCHASE';
    const isCSVExport = asyncPurchaseOrExportMsg === 'CSV_EXPORT';
    const isCRMExport = asyncPurchaseOrExportMsg === 'CRM_EXPORT';
    const isError = asyncPurchaseOrExportMsg === 'UNKNOWN_ERROR';

    const titleText = isPurchase
      ? 'Purchase Initiated'
      : isCSVExport
      ? 'CSV Export Initiated'
      : isCRMExport
      ? 'Export Initiated'
      : isError
      ? 'Aaah! Something went wrong'
      : '';

    const iconData = isError
      ? { Component: ErrorIcon }
      : isCSVExport
      ? { Component: CSVExportIcon }
      : { Component: PurchaseSuccess };

    const { Component: Icon, className: iconClassName } = iconData;

    const flags = { isPurchase, isCSVExport, isCRMExport };

    const showVerificationNote =
      isZBEnabled &&
      (exportSource === ADVANCED_SEARCH ||
        asyncPurchaseOrExportMsg === 'PURCHASE');

    const showWithoutEmailNote =
      (isCRMExport || isCSVExport) &&
      advSearchState.checkBoxFilters.showContactsWithoutEmail &&
      Utils.isCreditBasedSubscription();

    const showNoteList = showWithoutEmailNote || showVerificationNote;

    const description = isError ? (
      <ErrorDescription />
    ) : isZBEnabled && exportSource === ADVANCED_SEARCH ? (
      <>
        <DescriptionZB flags={flags} userEmail={userEmail} />
        {showNoteList && (
          <NoteList
            showWithoutEmailNote={showWithoutEmailNote}
            showVerificationNote={showVerificationNote}
          />
        )}
      </>
    ) : (
      <>
        <DescriptionNormal flags={flags} userEmail={userEmail} />
        {showNoteList && (
          <NoteList
            showWithoutEmailNote={showWithoutEmailNote}
            showVerificationNote={showVerificationNote}
          />
        )}
      </>
    );

    return (
      <CommonPopupWithImageComponent
        {...(Icon && { svgImgComponent: <Icon className={iconClassName} /> })}
        customParentClass={isError ? 'error' : 'purchase-popup async-popup'}
        titleText={titleText}
        description={description}
        btnText="OK"
        actionBtnCallback={handleHoldSuccessOkClick}
        clickOutsidePopupCallback={handleHoldSuccessOkClick}
        closeClickCallback={handleHoldSuccessOkClick}
        showCloseBtn
      />
    );
  }
  return null;
});

export default AsyncPurchaseExportMsgPopupComponent;
